import React, { useState, useEffect } from 'react';
import APP_VERSION from '../../version';
import { Layout, Menu, Button, Typography, theme, Space } from 'antd';
import {
  DashboardOutlined,
  CloudOutlined,
  SafetyOutlined,
  DatabaseOutlined,
  SearchOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  LogoutOutlined,
  AuditOutlined,
  BulbOutlined,
  BulbFilled,
  SettingOutlined,
  LockOutlined,
  AlertOutlined,
  FileZipOutlined,
} from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTheme } from '../../context/ThemeContext';

const { Header, Sider, Content } = Layout;
const { Title, Text } = Typography;

// ─── Route → Menu Key Mapping ─────────────────────────────────────────────────
//
// Maps URL pathnames to the menu item key that should be highlighted.
// We use this so clicking a link or typing a URL both highlight the right item.
//
const PATH_TO_SELECTED_KEY = {
  '/': 'dashboard',
  '/infrastructure': 'infrastructure',
  '/security-groups': 'sg-manage',
  '/security-groups/search': 'sg-search',
  '/backups': 'backups',
  '/audit': 'audit',
  '/settings': 'settings-general',
  '/feature-requests': 'feature-requests',
  '/health-events': 'health-events',
  '/mm-analyze': 'mm-analyze',
};

// ─── AppLayout Component ──────────────────────────────────────────────────────
//
// Props:
//   children  — the page component to render in the content area
//   user      — the Cognito user object (from getCurrentUser)
//   onSignOut — function to call when the user clicks Logout
//
const AppLayout = ({ children, user, onSignOut }) => {
  const [collapsed, setCollapsed] = useState(false);
  const [openMenuKeys, setOpenMenuKeys] = useState([]);
  const { isDark, setIsDark } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  // Access Ant Design's design token values (colors, radii, etc.)
  const {
    token: { colorBgContainer, borderRadiusLG },
  } = theme.useToken();

  // Highlight the correct menu item based on the current URL path
  const selectedKey = PATH_TO_SELECTED_KEY[location.pathname] || 'dashboard';

  // When the URL changes, open the correct submenu automatically.
  useEffect(() => {
    if (location.pathname.startsWith('/security-groups')) {
      setOpenMenuKeys(['security-groups']);
    } else if (location.pathname === '/settings') {
      setOpenMenuKeys(['settings']);
    } else if (['/audit', '/backups', '/feature-requests'].includes(location.pathname)) {
      setOpenMenuKeys(['admin']);
    } else {
      setOpenMenuKeys([]);
    }
  }, [location.pathname]);

  // ── Menu item definitions ──────────────────────────────────────────────────
  //
  // Each item has a key (used for highlighting), an icon, a label, and either
  // an onClick (for leaf items) or children (for submenus).
  //
  const menuItems = [
    {
      key: 'dashboard',
      icon: <DashboardOutlined />,
      label: 'Dashboard',
      onClick: () => navigate('/'),
    },
    {
      key: 'infrastructure',
      icon: <CloudOutlined />,
      label: 'Infrastructure',
      onClick: () => navigate('/infrastructure'),
    },
    {
      key: 'security-groups',
      icon: <SafetyOutlined />,
      label: 'Security Groups',
      // This item has children — it renders as an expandable submenu
      children: [
        {
          key: 'sg-manage',
          label: 'Manage Rules',
          onClick: () => navigate('/security-groups'),
        },
        {
          key: 'sg-search',
          icon: <SearchOutlined />,
          label: 'Search IPs',
          onClick: () => navigate('/security-groups/search'),
        },
      ],
    },
    {
      key: 'health-events',
      icon: <AlertOutlined />,
      label: 'Health Events',
      onClick: () => navigate('/health-events'),
    },
    {
      key: 'mm-analyze',
      icon: <FileZipOutlined />,
      label: 'MM Support Analyzer',
      onClick: () => navigate('/mm-analyze'),
    },
    {
      key: 'settings',
      icon: <SettingOutlined />,
      label: 'Settings',
      children: [
        {
          key: 'settings-general',
          label: 'General',
          onClick: () => navigate('/settings'),
        },
      ],
    },
    {
      key: 'admin',
      icon: <LockOutlined />,
      label: 'Admin',
      children: [
        {
          key: 'audit',
          icon: <AuditOutlined />,
          label: 'Audit Log',
          onClick: () => navigate('/audit'),
        },
        {
          key: 'backups',
          icon: <DatabaseOutlined />,
          label: 'Security Group Backups',
          onClick: () => navigate('/backups'),
        },
        {
          key: 'feature-requests',
          icon: <BulbOutlined />,
          label: 'Feature Requests',
          onClick: () => navigate('/feature-requests'),
        },
      ],
    },
  ];

  return (
    <Layout style={{ minHeight: '100vh' }}>

      {/* ── Sidebar (Sider) ──────────────────────────────────────────────── */}
      <Sider
        collapsible
        collapsed={collapsed}
        onCollapse={setCollapsed}
        trigger={null}        // We use our own toggle button in the header
        width={220}
        style={{ background: '#001529' }}
      >
        {/* Logo / branding area at the top of the sidebar */}
        <div
          style={{
            minHeight: 64,
            display: 'flex',
            alignItems: 'center',
            justifyContent: collapsed ? 'center' : 'flex-start',
            padding: collapsed ? '0' : '12px 20px',
            borderBottom: '1px solid rgba(255,255,255,0.08)',
            overflow: 'hidden',
          }}
        >
          <CloudOutlined style={{ color: '#E8741A', fontSize: 22, flexShrink: 0 }} />
          {!collapsed && (
            <Text
              strong
              style={{
                color: '#fff',
                marginLeft: 10,
                fontSize: 13,
                lineHeight: 1.3,
              }}
            >
              Infrastructure Management System
            </Text>
          )}
        </div>

        {/* Navigation menu */}
        <style>{`
          .itgi-nav .ant-menu-title-content { white-space: normal; line-height: 1.4; }
          .itgi-nav .ant-menu-item,
          .itgi-nav .ant-menu-submenu-title { height: auto !important; min-height: 40px; padding-block: 8px !important; }
        `}</style>
        <Menu
          theme="dark"
          mode="inline"
          inlineCollapsed={collapsed}
          selectedKeys={[selectedKey]}
          openKeys={openMenuKeys}
          onOpenChange={setOpenMenuKeys}
          items={menuItems}
          className="itgi-nav"
          style={{ marginTop: 8, borderRight: 0 }}
        />
      </Sider>

      {/* ── Right side: Header + Content ─────────────────────────────────── */}
      <Layout>

        {/* ── Top Header ──────────────────────────────────────────────────── */}
        <Header
          style={{
            background: colorBgContainer,
            padding: '0 24px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: '1px solid #f0f0f0',
            // Sticky so the header stays visible when the user scrolls down
            position: 'sticky',
            top: 0,
            zIndex: 100,
          }}
        >
          {/* Left side: sidebar toggle + app title */}
          <Space align="center" size={16}>
            <Button
              type="text"
              icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
              onClick={() => setCollapsed(!collapsed)}
              style={{ fontSize: 16, width: 32, height: 32 }}
            />
            <Title level={5} style={{ margin: 0, whiteSpace: 'nowrap' }}>
              Infrastructure Management System
            </Title>
          </Space>

          {/* Right side: version + user email + theme toggle + Logout */}
          <Space align="center" size={12}>
            <Text type="secondary" style={{ fontSize: 11, opacity: 0.6 }}>
              v{APP_VERSION}
            </Text>
            {user && (
              <Text type="secondary" style={{ fontSize: 13 }}>
                {user.signInDetails?.loginId || user.username}
              </Text>
            )}
            <Button
              type="text"
              icon={isDark ? <BulbFilled style={{ color: '#fadb14' }} /> : <BulbOutlined />}
              onClick={() => setIsDark(!isDark)}
              size="small"
              title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
            />
            <Button
              icon={<LogoutOutlined />}
              onClick={onSignOut}
              size="small"
            >
              Logout
            </Button>
          </Space>
        </Header>

        {/* ── Main Content Area ────────────────────────────────────────────── */}
        {/*
          The page component (Dashboard, Infrastructure, etc.) renders here.
          We give it a white card-like background with padding and rounded corners.
        */}
        <Content
          style={{
            margin: 24,
            padding: 24,
            background: colorBgContainer,
            borderRadius: borderRadiusLG,
            // Ensure the content area is tall enough to fill the viewport
            minHeight: 'calc(100vh - 64px - 48px)',
          }}
        >
          {children}
        </Content>

      </Layout>
    </Layout>
  );
};

export default AppLayout;
